import { useMemo, useState, type ReactNode } from 'react';
import type {
  BusSeatFailure,
  BusSeatFloorView,
  BusSeatMapState,
  BusSegmentPick,
} from '../models.js';
import { BusSeatPicker, type BusSeatPickerProps } from './bus_seat_picker.js';
import { preselectBusSeats, type BusSeatPreference } from './bus_seat_preselect.js';

/** Quem viaja, na ordem da reserva: o nome curto no detalhe e as iniciais na
 *  poltrona escolhida. */
export type BusSeatChoiceTraveler = {
  readonly id: string;
  readonly name: string;
  readonly initials: string;
};

/** A planta da viagem como o fornecedor a devolve: um mapa por trecho
 *  (1 = direto ou 1º trecho). Estrutural — o BFF e o agente cabem nela. */
export type BusSeatsSource = {
  readonly segments: readonly {
    readonly segment: number;
    readonly floors: readonly BusSeatFloorView[];
  }[];
};

/** Quantos trechos a viagem tem: a planta manda quando chegou; sem ela, as
 *  conexões da oferta (0 = direto, um trecho). */
export function busSegmentCount(connections: number, seats: BusSeatsSource | undefined): number {
  return Math.max(connections + 1, seats?.segments.length ?? 0);
}

/** A falha da planta com o motivo — o que o host rejeita para a escolha dizer
 *  por que não carregou. */
export class BusSeatsError extends Error {
  constructor(
    readonly reason: BusSeatFailure,
    options?: { cause?: unknown },
  ) {
    super(`bus seats failed: ${reason}`, options);
    this.name = 'BusSeatsError';
  }
}

/** O motivo de uma falha da planta: o do `BusSeatsError`; qualquer outra é o
 *  fornecedor. */
export function busSeatFailureOf(error: unknown): BusSeatFailure {
  return error instanceof BusSeatsError ? error.reason : 'unavailable';
}

/** O estado da planta de cada trecho, na ordem da viagem — o que o
 *  `BusSeatPicker` desenha. A chamada que falhou leva o motivo (`failure`,
 *  o fornecedor por padrão) a todo trecho; trecho que a resposta não trouxe
 *  não tem marcação de assento (`notOffered`). Nos dois, a saída é "seguir sem
 *  assento". */
export function busSeatMapStates(
  connections: number,
  seats: {
    status: 'pending' | 'error' | 'success';
    data?: BusSeatsSource | undefined;
    failure?: BusSeatFailure;
  },
): BusSeatMapState[] {
  return Array.from({ length: busSegmentCount(connections, seats.data) }, (_, index) => {
    if (seats.status === 'pending') return { kind: 'loading' };
    if (seats.status === 'error') return { kind: 'error', reason: seats.failure ?? 'unavailable' };
    const segment = seats.data?.segments.find((candidate) => candidate.segment === index + 1);
    return segment ? { kind: 'ready', floors: segment.floors } : { kind: 'notOffered' };
  });
}

type Picks = readonly (BusSegmentPick | undefined)[];

/** As marcações são a sugestão das Preferências, trecho a trecho. */
function samePicks(a: Picks, b: Picks): boolean {
  return a.length === b.length && a.every((pick, index) => sameJson(pick, b[index]));
}

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Marca UMA vez, quando as plantas chegam, as poltronas que as Preferências de
 * ônibus sugerem (posição na fileira, terço do ônibus, sentar juntos) — em cada
 * trecho. Só marca: quem confirma é o `Selecionar`. Não marca por cima de uma
 * escolha que a pessoa já fez, nem sem as preferências lidas (`undefined` =
 * sem sugestão). Devolve a sugestão aplicada, para o rótulo.
 */
function usePreselectedSeats(
  segments: readonly BusSeatMapState[],
  travelerIds: readonly string[],
  preference: BusSeatPreference | undefined,
  setPicks: (update: (current: Picks) => Picks) => void,
): Picks | undefined {
  // Once, during render, as soon as the plants and the preferences are in.
  const [done, setDone] = useState(false);
  const [applied, setApplied] = useState<Picks>();
  const loading = segments.some((segment) => segment.kind === 'loading');
  if (!done && preference && !loading) {
    setDone(true);
    const suggested = segments.map((segment) =>
      segment.kind === 'ready'
        ? preselectBusSeats(segment.floors, travelerIds, preference)
        : undefined,
    );
    if (suggested.some(Boolean)) {
      setPicks((current) => (current.some(Boolean) ? current : suggested));
      // Se a pessoa já tinha escolhido, as marcações não são a sugestão e o rótulo
      // não aparece (`samePicks`).
      setApplied(suggested);
    }
  }
  return applied;
}

/** Todos os trechos resolvidos, na ordem — ou `null` enquanto falta algum. */
function resolvedSegments(picks: Picks, count: number): BusSegmentPick[] | null {
  const segments = picks
    .slice(0, count)
    .filter((pick): pick is BusSegmentPick => pick !== undefined);
  return segments.length === count ? segments : null;
}

export type BusSeatChoiceProps = Pick<
  BusSeatPickerProps,
  'segments' | 'seatClassLabel' | 'price' | 'copy' | 'layout' | 'seatGlyph' | 'busy' | 'onRetry'
> & {
  /** Uma poltrona por viajante em cada trecho. */
  travelers: readonly BusSeatChoiceTraveler[];
  /** As Preferências de ônibus lidas; `undefined` = sem sugestão. */
  preference?: BusSeatPreference;
  /** "Assento recomendado com base nas suas preferências" — aparece enquanto as
   *  marcações SÃO a sugestão. */
  suggestionLabel: string;
  /** A perna fechada: a resolução de cada trecho, na ordem da viagem. */
  onConfirm(segments: BusSegmentPick[]): void;
  onCollapse(): void;
};

/**
 * A escolha de poltrona POR TRECHO de uma viagem — a mesma na bancada e no card
 * da conversa: o `BusSeatPicker` com as marcações sugeridas pelas Preferências
 * de ônibus, e o `Selecionar` que só fecha a perna com todo trecho resolvido
 * (uma poltrona por viajante, ou "seguir sem assento"). A planta chega pronta
 * do host (`busSeatMapStates`): o kit não busca nada.
 */
export function BusSeatChoice({
  travelers,
  preference,
  suggestionLabel,
  onConfirm,
  ...picker
}: Readonly<BusSeatChoiceProps>): ReactNode {
  const [picks, setPicks] = useState<Picks>([]);
  const travelerIds = useMemo(() => travelers.map((person) => person.id), [travelers]);
  const seatPeople = useMemo(
    () =>
      Object.fromEntries(
        travelers.map(({ id, name, initials }) => [id, { name, initials }] as const),
      ),
    [travelers],
  );
  const suggested = usePreselectedSeats(picker.segments, travelerIds, preference, setPicks);
  // O rótulo vale enquanto as marcações SÃO a sugestão: mexeu, ele sai.
  const showSuggestion = suggested !== undefined && samePicks(picks, suggested);
  return (
    <BusSeatPicker
      {...picker}
      travelerIds={travelerIds}
      picks={picks}
      travelers={seatPeople}
      {...(suggested ? { recommended: suggested } : {})}
      {...(showSuggestion ? { suggestionLabel } : {})}
      onPicksChange={setPicks}
      onConfirm={() => {
        const resolved = resolvedSegments(picks, picker.segments.length);
        if (resolved) onConfirm(resolved);
      }}
    />
  );
}
